// LogsList.tsx
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import { TContext, TFilterModel } from "../../../types/config";
import { eREGIONS, eDATE_FORMAT, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { defaultFilterParams } from "../../../utils/constants";
import { GetAuditLogList } from "../../../utils/api.constant";
import { TLog } from "../../../types";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import {
  FilterFilled,
  FilterOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import Highlighter from "react-highlight-words";
import { FilterDropdownProps } from "antd/es/table/interface";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import { UserContext } from "../../../context";
import { useColumnSearch } from "../../../components/table/column-search";

const LOG_LIST_COLUMN_KEY = "logList";
const defaultFromDate = dayjs().subtract(90, "day");
const defaultToDate = dayjs();
const { RangePicker } = DatePicker;

export default function LogsList() {
  // initialization of hooks
  const { post } = useFetch(); // custom hook used to call the apis
  const [isLoading, setIsLoading] = useState(true); // to manage the loader
  const [data, setData] = useState<TLog[]>([]); // to store config group data
  const [filterParams, setFilterParams] = useState({
    fromDate: defaultFromDate.format("YYYY-MM-DD"),
    toDate: defaultToDate.format("YYYY-MM-DD"),
  });
  const { onShowToast } = useToast(); // custom hook used to show toast
  const searchInput = useRef<InputRef>(null);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const context: TContext = useContext(UserContext);
  const { getDateRangeFilterProps } = useColumnSearch<TLog>();
  const handleReset = (clearFilters: FilterDropdownProps["clearFilters"]) => {
    if (clearFilters) {
      clearFilters();
    }
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: any): any => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e: any) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e: any) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: any,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const [columns, setColumns] = useState<any[]>([]);
  const [initialColumns, setInitialColumns] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  const getInitialColumns = (): any[] => [
    {
      title: "Log Date",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: "15%",
      filterSearch: true,
      ...getDateRangeFilterProps("createdOn"),
      sorter: (a: TLog, b: TLog) =>
        String(a.createdOn)?.localeCompare(String(b.createdOn)),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, context?.user?.dateFormat, true);
      },
    },
    {
      title: "Action Description",
      dataIndex: "auditMessage",
      key: "auditMessage",
      ...getColumnSearchProps("auditMessage"),
      // width: "50%",
      filterSearch: true,
      sorter: (a: TLog, b: TLog) =>
        a.auditMessage.localeCompare(b.auditMessage),
      onFilter: (value: string | number | boolean, record: TLog) =>
        record.auditMessage.includes(value as string),
    },
    {
      title: "Action Taken By",
      dataIndex: "actionByUserName",
      key: "actionByUserName",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TLog, b: TLog) =>
        a?.actionByUserName.localeCompare(b?.actionByUserName),
      onFilter: (value: string | number | boolean, record: TLog) =>
        record.actionByUserName?.toLowerCase() ===
        (value as string).toLowerCase(),
    },
    {
      title: "Organization",
      dataIndex: "orgName",
      key: "orgName",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TLog, b: TLog) => a?.orgName.localeCompare(b?.orgName),
      onFilter: (value: string | number | boolean, record: TLog) =>
        record.orgName?.toLowerCase() === (value as string).toLowerCase(),
    },
  ];

  useEffect(() => {
    const cols = getInitialColumns();
    setInitialColumns(cols);

    (async () => {
      const savedPreferences = await loadColumnPreferences(LOG_LIST_COLUMN_KEY);
      if (savedPreferences) {
        const initialColumnsMap = new Map(cols.map((col) => [col.key, col]));
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      } else {
        setColumns(cols);
        setDraftColumns(cols);
      }
    })();
  }, [context.user]);

  useEffect(() => {
    fetchData();
  }, [filterParams.fromDate, filterParams.toDate]);

  const filteredData = data.filter((item) => {
    const globalsearchMatch =
      !globalsearchText ||
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(globalsearchText.toLowerCase()),
      );

    // Dynamic check for all active filters
    const filterMatches = Object.entries(activeFilters).every(
      ([filterKey, filterValue]) => {
        if (!filterValue || filterValue.length === 0) return true;

        // Date Range Match (nested dayjs arrays)
        if (
          filterValue.length > 0 &&
          Array.isArray(filterValue[0]) &&
          filterValue[0].length === 2
        ) {
          const startDayjs = filterValue[0][0];
          const endDayjs = filterValue[0][1];
          if (startDayjs && endDayjs && "valueOf" in startDayjs) {
            const start = new Date(startDayjs.valueOf()).setHours(0, 0, 0, 0);
            const end = new Date(endDayjs.valueOf()).setHours(23, 59, 59, 999);
            const itemDateVal = item[filterKey as keyof TLog];
            if (!itemDateVal) return false;
            const rowDate = new Date(itemDateVal as string).getTime();
            return rowDate >= start && rowDate <= end;
          }
        }

        // Dropdown or Search Match
        const column = initialColumns.find(
          (col) => col.key === filterKey || col.dataIndex === filterKey,
        );
        if (column && column.onFilter) {
          return filterValue.some((val: any) => column.onFilter!(val, item));
        }

        const itemValue = item[filterKey as keyof TLog];
        return filterValue.includes(itemValue);
      },
    );

    return globalsearchMatch && filterMatches;
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
          fromDate: filterParams.fromDate,
          toDate: filterParams.toDate,
        },
      };

      const response = await post(GetAuditLogList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TLog, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          createdOn: item.createdOn,
          auditMessage: item.auditMessage,
          actionByUserName: item.actionByUserName,
          orgName: item.orgName,
        }));
        setData(formattedData);
        setIsLoading(false);
        updateColumnFilters(data, activeFilters, globalsearchText);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (
    dataset: TLog[],
    currentFilters: Record<string, any>,
    currentSearchText: string,
  ) => {
    const getFilters = (key: keyof TLog) => {
      const validRowsForThisColumn = dataset.filter((item) => {
        // Global search match
        const searchMatch =
          !currentSearchText ||
          Object.values(item).some((val) =>
            String(val).toLowerCase().includes(currentSearchText.toLowerCase()),
          );

        // Check each active filter
        const filterMatches = Object.entries(currentFilters).every(
          ([filterKey, filterValue]) => {
            if (!filterValue || filterValue.length === 0 || filterKey === key)
              return true;

            // Date Range Match (nested dayjs arrays)
            if (
              filterValue.length > 0 &&
              Array.isArray(filterValue[0]) &&
              filterValue[0].length === 2
            ) {
              const startDayjs = filterValue[0][0];
              const endDayjs = filterValue[0][1];
              if (startDayjs && endDayjs && "valueOf" in startDayjs) {
                const start = new Date(startDayjs.valueOf()).setHours(
                  0,
                  0,
                  0,
                  0,
                );
                const end = new Date(endDayjs.valueOf()).setHours(
                  23,
                  59,
                  59,
                  999,
                );
                const itemDateVal = item[filterKey as keyof TLog];
                if (!itemDateVal) return false;
                const rowDate = new Date(itemDateVal as string).getTime();
                return rowDate >= start && rowDate <= end;
              }
            }

            const column = initialColumns.find(
              (col) => col.key === filterKey || col.dataIndex === filterKey,
            );
            if (column && column.onFilter) {
              return filterValue.some((val: any) =>
                column.onFilter!(val, item),
              );
            }

            const itemValue = item[filterKey as keyof TLog];
            return filterValue.includes(itemValue);
          },
        );

        return searchMatch && filterMatches;
      });

      const uniqueValues = Array.from(
        new Set(
          validRowsForThisColumn
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-",
            ) as string[],
        ),
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };

    const actionByUserNameFilters = getFilters("actionByUserName");
    const orgNameFilters = getFilters("orgName");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "actionByUserName") {
          return { ...col, filters: actionByUserNameFilters };
        }
        if (col.key === "orgName") {
          return { ...col, filters: orgNameFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    updateColumnFilters(data, activeFilters, globalsearchText);
  }, [activeFilters, data, globalsearchText]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(LOG_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(LOG_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const handleResetAll = () => {
    setActiveFilters({});
    setGlobalSearchText("");
    setFilterParams({
      fromDate: defaultFromDate.format("YYYY-MM-DD"),
      toDate: defaultToDate.format("YYYY-MM-DD"),
    });
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item))),
  );

  const formatExportRow = (item: any) => {
    const mapped = {
      ...item,
      createdOn: DateUtils.formatForExport(
        item?.createdOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
      updatedOn: DateUtils.formatForExport(
        item?.updatedOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
    };

    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  };

  const exportData = data.map(formatExportRow);
  const exportFilteredData = filteredData.map(formatExportRow);

  return (
    <div className="pageBody">
      <PageHeader
        // icon={<FaObjectGroup />}
        title={`${filteredData?.length} Total Audit Logs`}
        size="large"
      >
        <Space>
          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <RangePicker
            format={context?.user?.dateFormat?.toUpperCase()}
            size={"small"}
            allowClear={false}
            value={[dayjs(filterParams.fromDate), dayjs(filterParams.toDate)]}
            onChange={(dates) => {
              if (dates) {
                setFilterParams((prev) => ({
                  ...prev,
                  fromDate: dates[0] ? dates[0].format("YYYY-MM-DD") : "",
                  toDate: dates[1] ? dates[1].format("YYYY-MM-DD") : "",
                }));
              }
            }}
          />
          <ExportToCSV
            allColumns={initialColumns}
            data={exportData}
            filteredData={exportFilteredData}
            visibleColumns={visibleColumns}
            moduleName="Audit Logs"
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{ x: "max-content" }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
